import React, { useEffect, useRef, useState } from 'react';

import MarkerPremium from 'assets/images/ab-map.png';
import ClusterPremium from 'assets/images/ab-mapCluster.png';
import ClusterFree from 'assets/images/ab-mapClusterGrey.png';
import MarkerFree from 'assets/images/ab-mapGrey.png';

import type { LatLng } from 'hooks/useDirectoryConfig/types';
import type { FreeHotelItem, PremiumHotelItem } from 'hooks/useDirectoryData/types';

import type { ContainerStyle } from '../constants';

interface ClusterStyle {
  url: string;
  width: number;
  height: number;
  textColor: string;
  textSize: number;
  fontFamily: string;
}

interface ClusterOptions {
  styles: ClusterStyle[];
  clusterClass: string;
}

import { GoogleMap, InfoWindow, Marker, MarkerClusterer, useJsApiLoader } from '@react-google-maps/api';

import { MapCard } from './MapCard/MapCard';

type HoveredMarker = { kind: 'premium'; hotel: PremiumHotelItem } | { kind: 'free'; hotel: FreeHotelItem } | null;

const CLUSTER_STYLE = {
  width: 35,
  height: 51,
  textColor: 'white',
  textSize: 14,
  fontFamily: 'Source Sans Pro',
} as const;

// The default 'auto' resolves to 'greedy' here because the directory page itself doesn't scroll.
const MAP_OPTIONS: google.maps.MapOptions = {
  gestureHandling: 'cooperative',
};

const clusterPremium: ClusterOptions = {
  styles: [{ url: ClusterPremium, ...CLUSTER_STYLE }],
  clusterClass: 'clusterIcon',
};

const clusterFree: ClusterOptions = {
  styles: [{ url: ClusterFree, ...CLUSTER_STYLE }],
  clusterClass: 'clusterIcon',
};

interface DirectoryMapProps {
  googleMapsApiKey: string;
  containerStyle: ContainerStyle;
  center: LatLng;
  zoom: number;
  premiumHotelsList: PremiumHotelItem[];
  freeHotelsList: FreeHotelItem[];
  onBoundsChanged: (bounds: google.maps.LatLngBounds, zoom: number) => void;
  onMarkerClick: (lat: number | string, lon: number | string) => void;
  onPremiumInfo: (userInfoID: string, name: string) => void;
  onFreeInfo: (listingId: string, name: string) => void;
  showMarkerTooltip?: boolean;
  onMapLoad?: () => void;
}

function DirectoryMap({
  googleMapsApiKey,
  containerStyle,
  center,
  zoom,
  premiumHotelsList,
  freeHotelsList,
  onBoundsChanged,
  onMarkerClick,
  onPremiumInfo,
  onFreeInfo,
  showMarkerTooltip = false,
  onMapLoad,
}: DirectoryMapProps) {
  const { isLoaded, loadError } = useJsApiLoader({ id: 'google-map-script', googleMapsApiKey });
  const mapRef = useRef<google.maps.Map | null>(null);
  const [hoveredMarker, setHoveredMarker] = useState<HoveredMarker>(null);
  const [hasValidBounds, setHasValidBounds] = useState(false);
  const [mapsAuthFailed, setMapsAuthFailed] = useState(false);
  const mapsAuthFailedRef = useRef(false);
  const dismissTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  type GmWindow = Window & { gm_authFailure?: () => void };

  useEffect(() => {
    const prev = (window as GmWindow).gm_authFailure;
    (window as GmWindow).gm_authFailure = () => {
      mapsAuthFailedRef.current = true;
      setMapsAuthFailed(true);
      prev?.();
    };
    return () => {
      (window as GmWindow).gm_authFailure = prev;
    };
  }, []);

  const scheduleHide = () => {
    dismissTimer.current = setTimeout(() => {
      return setHoveredMarker(null);
    }, 150);
  };

  const cancelHide = () => {
    if (dismissTimer.current !== null) {
      clearTimeout(dismissTimer.current);
      dismissTimer.current = null;
    }
  };

  const handleIdle = () => {
    if (mapsAuthFailedRef.current) return;
    if (mapRef.current) {
      const bounds = mapRef.current.getBounds();
      const currentZoom = mapRef.current.getZoom();
      if (bounds && currentZoom !== undefined) {
        setHasValidBounds(true);
        onBoundsChanged(bounds, currentZoom);
      }
    }
  };

  if (loadError || mapsAuthFailed) {
    // TODO: also report to Sentry via Raven.captureException once integrated for this component
    console.error('[DirectoryMap] Google Maps API failed to load — domain may not be whitelisted', loadError);
    return (
      <div className="rightSide map-error">
        <span className="map-error__message">{'Map could not be loaded'}</span>
      </div>
    );
  }

  if (!isLoaded) return <div className="rightSide" />;

  return (
    <div className="rightSide">
      <GoogleMap
        mapContainerStyle={containerStyle}
        center={center}
        zoom={zoom}
        options={MAP_OPTIONS}
        onLoad={(map) => {
          mapRef.current = map;
          onMapLoad?.();
        }}
        onIdle={handleIdle}
      >
        {hasValidBounds && (
          <MarkerClusterer minimumClusterSize={4} gridSize={100} options={clusterFree}>
            {(clusterer) => {
              return (
                <React.Fragment>
                  {freeHotelsList.map((hotel) => {
                    return (
                      <Marker
                        key={hotel.listing_id}
                        position={{ lat: Number(hotel.latitude), lng: Number(hotel.longitude) }}
                        icon={MarkerFree}
                        onClick={() => {
                          onMarkerClick(hotel.latitude, hotel.longitude);
                        }}
                        onMouseOver={() => {
                          cancelHide();
                          if (showMarkerTooltip) setHoveredMarker({ kind: 'free', hotel });
                        }}
                        onMouseOut={scheduleHide}
                        clusterer={clusterer}
                      />
                    );
                  })}
                </React.Fragment>
              );
            }}
          </MarkerClusterer>
        )}
        {hasValidBounds && (
          <MarkerClusterer minimumClusterSize={5} options={clusterPremium}>
            {(clusterer) => {
              return (
                <React.Fragment>
                  {premiumHotelsList.map((hotel) => {
                    return (
                      <Marker
                        key={hotel.id}
                        position={{ lat: hotel.coords.latitude, lng: hotel.coords.longitude }}
                        icon={MarkerPremium}
                        onClick={() => {
                          onMarkerClick(hotel.coords.latitude, hotel.coords.longitude);
                        }}
                        onMouseOver={() => {
                          cancelHide();
                          if (showMarkerTooltip) setHoveredMarker({ kind: 'premium', hotel });
                        }}
                        onMouseOut={scheduleHide}
                        clusterer={clusterer}
                      />
                    );
                  })}
                </React.Fragment>
              );
            }}
          </MarkerClusterer>
        )}
        {hasValidBounds && showMarkerTooltip && hoveredMarker && (
          <InfoWindow
            position={
              hoveredMarker.kind === 'premium'
                ? { lat: hoveredMarker.hotel.coords.latitude, lng: hoveredMarker.hotel.coords.longitude }
                : { lat: Number(hoveredMarker.hotel.latitude), lng: Number(hoveredMarker.hotel.longitude) }
            }
            options={{ disableAutoPan: true, pixelOffset: new google.maps.Size(0, -55) }}
            onCloseClick={() => {
              return setHoveredMarker(null);
            }}
          >
            <MapCard
              marker={hoveredMarker}
              onViewProfile={() => {
                if (hoveredMarker.kind === 'premium') {
                  onPremiumInfo(hoveredMarker.hotel.userInfoID, hoveredMarker.hotel.propertyName);
                } else {
                  onFreeInfo(hoveredMarker.hotel.listing_id.toString(), hoveredMarker.hotel.company);
                }
              }}
              onMouseEnter={cancelHide}
              onMouseLeave={scheduleHide}
            />
          </InfoWindow>
        )}
      </GoogleMap>
    </div>
  );
}

export default DirectoryMap;
